const { formatMoney } = window.BAO.Utils

class ProductEngraving extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    this.handleSelectChange = this.handleSelectChange.bind(this)
    this.handleProductSelectChange = this.handleProductSelectChange.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleApply = this.handleApply.bind(this)

    this.els.select?.elements.forEach((selectEl) => {
      this.listeners.add(selectEl, 'change', this.handleSelectChange)
    })

    this.els.product?.elements.forEach((selectProductEl) => {
      this.listeners.add(
        selectProductEl,
        'change',
        this.handleProductSelectChange,
      )
    })

    this.els.input?.elements.forEach((inputEl) => {
      this.listeners.add(inputEl, 'input', this.handleInputChange)
    })
    this.listeners.add(this.els.apply.element, 'click', this.handleApply)

    this.handleReset = this.handleReset.bind(this)
    this.listeners.add(this.els.reset.element, 'click', this.handleReset)
  }

  handleInputChange (event) {
    const inputEl = event.target.closest(this.els?.input?.selector)
    if (!inputEl) return

    let count

    // Use Intl.Segmenter if available for accurate grapheme cluster segmentation
    if (Intl && Intl.Segmenter) {
      const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' })
      count = [...segmenter.segment(inputEl.value)].length
    } else {
      // Fallback to spread operator for emoji handling
      count = [...inputEl.value].length
    }

    // Update character count display
    const countEl = this.els.count.elements.find(
      (countEl) => countEl.dataset.field === inputEl.dataset.field,
    )

    if (countEl) {
      countEl.innerHTML = count
    }

    // Validate emojis in the input
    let hasEmojis = false
    for (let index = 0; index < this.els.input?.elements.length; index++) {
      const element = this.els.input?.elements[index]
      hasEmojis = this.handleEmojiChecker(element)

      if (hasEmojis) {
        break
      }
    }

    // Check if the input value contains emojis
    const applyButton = this.els.apply.element
    applyButton.disabled = hasEmojis
  }

  handleEmojiChecker (element) {
    const emojiCharacters = /[\p{Emoji}]/gu // Match all emojis (ES2021+)
    const allowedValues = [
      '❤',
      '❤️',
      '⭐',
      ...Array.from({ length: 10 }, (_, i) => i.toString()),
    ] // Allowed emojis with variations, numbers 0-9
    const inputValue = element.value
    const inputElContainer = element.closest('.drw-Personalisation_Item')
    const foundEmojis = inputValue.match(emojiCharacters) || []
    const normalizeEmoji = (emoji) => emoji.normalize('NFC')

    const disallowedValues = foundEmojis.filter(
      (emoji) => !allowedValues.includes(normalizeEmoji(emoji)),
    )

    if (disallowedValues.length > 0) {
      if (!inputElContainer.querySelector('.drw-Personalisation_ItemError')) {
        const errorMessage = document.createElement('p')

        errorMessage.textContent =
          window.theme.strings.emojiPersonalisationErrorMessage
        errorMessage.className = 'drw-Personalisation_ItemError'

        inputElContainer.appendChild(errorMessage)
      }

      return true
    } else {
      if (inputElContainer.querySelector('.drw-Personalisation_ItemError')) {
        inputElContainer
          .querySelector('.drw-Personalisation_ItemError')
          .remove()
      }

      return false
    }
  }

  handleProductSelectChange (event) {
    // get the selected option and get the value
    const selectedOption = event.target.options[event.target.selectedIndex]
    const selectedOptionValue = selectedOption.value

    if (selectedOption.value !== undefined && selectedOption.value !== '') {
      const matchingAttribute = Array.from(this.attributes).find(
        (attr) =>
          attr.name.startsWith('data-additional-product-id-') &&
          attr.value === selectedOptionValue,
      )

      if (matchingAttribute) {
        const matchingElements = this.querySelectorAll(
          `[${matchingAttribute.name}]`,
        )
        matchingElements.forEach((element) => {
          element.setAttribute('aria-hidden', false)
        })
      }
    } else {
      // Find all elements with a data-product-engraving-el="optionalProduct" and set aria-hidden to true except the first instance
      const matchingElements = this.querySelectorAll(
        `[data-product-engraving-el="optionalProduct"]`,
      )
      matchingElements.forEach((element, index) => {
        if (index !== 0) {
          element.setAttribute('aria-hidden', true)

          // Find the input element within and remove any values applied
          const inputEl = element.querySelector('input')
          if (inputEl) {
            inputEl.value = ''
          }
        }
      })
    }
  }

  handleReset (event) {
    this.els.product?.elements.forEach((productEl) => {
      productEl.value = ''
      productEl.dispatchEvent(new Event('change'))
    })

    this.els.input?.elements.forEach((inputEl) => {
      inputEl.value = ''
      inputEl.dispatchEvent(new Event('input'))
    })

    this.els.select?.elements.forEach((selectEl) => {
      this.requiredFields(selectEl, false)
    })

    this.handleApply()
  }

  handleSelectChange (event) {
    const matchedSelect = this.els.select?.elements.find((selectEl) =>
      selectEl.value.includes(':: Additional Cost'),
    )

    if (event.target.value.length > 0) {
      this.requiredFields(event.target, false)
    }

    this.els.cost?.elements.forEach((costEl) => {
      costEl.setAttribute('aria-hidden', !matchedSelect)
    })

    this.setAttribute('data-additional-costs', !!matchedSelect)
  }

  handleApply (e) {
    this.required = false
    let inputsPopulated = !this.els.input?.exists
    let personalisationCost = 0
    let additionalCost = 0

    this.els.product?.elements.forEach((productEl) => {
      const matchedHiddenEl = this.externalEls.hiddeninput.elements.find(
        (hiddenEl) =>
          hiddenEl.dataset.field === productEl.dataset.field &&
          hiddenEl.dataset.formId === this.formId,
      )

      if (productEl.value !== '') {
        personalisationCost =
          personalisationCost +
          parseInt(productEl.options[productEl.selectedIndex].dataset.cost)
        if (!productEl.hasAttribute('product-form-personalisation-required')) {
          additionalCost =
            additionalCost +
            parseInt(productEl.options[productEl.selectedIndex].dataset.cost)
        }
      }

      const filteredEls = this.externalEls.additionalproduct?.elements.filter(
        (additionalproductEl) =>
          additionalproductEl.dataset.formId === this.formId,
      )
      filteredEls?.forEach((additionalProductEl) => {
        if (additionalProductEl.dataset.field === productEl.dataset.field) {
          if (productEl.value !== '') {
            additionalProductEl.removeAttribute('disabled')

            if (additionalProductEl.dataset.addValue === 'true') {
              additionalProductEl.value = productEl.value
            }
          } else {
            additionalProductEl.setAttribute('disabled', '')
          }
        }
      })

      if (productEl.value !== '') {
        matchedHiddenEl.value =
          productEl.options[productEl.selectedIndex].dataset.name
        matchedHiddenEl.removeAttribute('disabled')
      } else {
        matchedHiddenEl.value = ''
        matchedHiddenEl.setAttribute('disabled', '')
      }
    })

    const filteredHiddenInputEls = this.externalEls.hiddeninput.elements.filter(
      (hiddeninputEl) => hiddeninputEl.dataset.formId === this.formId,
    )

    this.els.input?.elements.forEach((inputEl) => {
      const matchedHiddenEl = filteredHiddenInputEls.find(
        (hiddenEl) => hiddenEl.dataset.field === inputEl.dataset.field,
      )

      if (
        this.addCharges &&
        inputEl.value !== '' &&
        inputEl.dataset.additionalCost !== undefined
      ) {
        personalisationCost =
          personalisationCost + parseInt(inputEl.dataset.additionalCost)
      }

      const filteredAdditionalChargeEls =
        this.externalEls.additionalcharge?.elements.filter(
          (additionalchargeEl) =>
            additionalchargeEl.dataset.formId === this.formId,
        )
      filteredAdditionalChargeEls?.forEach((additionalChargeEl) => {
        if (additionalChargeEl.dataset.field === inputEl.dataset.field) {
          if (inputEl.value !== '' && this.addCharges) {
            additionalChargeEl.removeAttribute('disabled')
            if (additionalChargeEl.dataset.addValue === 'true') {
              additionalChargeEl.value = inputEl.value
            }
          } else {
            additionalChargeEl.setAttribute('disabled', '')
          }
        }
      })

      if (inputEl.value === '') {
        matchedHiddenEl.value = ''
        matchedHiddenEl.setAttribute('disabled', '')
      } else {
        matchedHiddenEl.value = inputEl.value
        matchedHiddenEl.removeAttribute('disabled')

        if (inputEl.dataset.linkedHandles) {
          const linkedHandles = inputEl.dataset.linkedHandles.split(',')
          this.els.select?.elements.forEach((selectEl) => {
            if (
              linkedHandles.includes(selectEl.dataset.handle) &&
              selectEl.value.length === 0
            ) {
              this.requiredFields(selectEl)
              this.required = true
            }
          })
        }

        inputsPopulated = true
      }
    })

    this.els.select?.elements.forEach((selectEl) => {
      const matchedHiddenEl = filteredHiddenInputEls.find(
        (hiddenEl) => hiddenEl.dataset.field === selectEl.dataset.field,
      )

      if (inputsPopulated) {
        matchedHiddenEl.value = selectEl.value
        matchedHiddenEl.removeAttribute('disabled')
      } else {
        matchedHiddenEl.value = ''
        matchedHiddenEl.setAttribute('disabled', '')
      }
    })

    const triggerEl = this.externalEls.trigger.elements.find(
      (triggerEl) => triggerEl.dataset.formId === this.formId,
    )
    const triggerLabelEl = this.externalEls.triggerlabel.elements.find(
      (triggerlabelEl) => triggerlabelEl.dataset.formId === this.formId,
    )
    const triggerCostEl = this.externalEls.cost.elements.find(
      (costEl) => costEl.dataset.formId === this.formId,
    )

    triggerEl.classList.toggle('prd-Form_Variant-required', false)

    if (inputsPopulated) {
      triggerLabelEl.classList.toggle('prd-Form_VariantText-light', false)
      triggerLabelEl.textContent = triggerLabelEl.dataset.edit

      if (additionalCost > 0) {
        triggerCostEl.textContent = `+${formatMoney(additionalCost)}`
      } else {
        triggerCostEl.textContent = ''
      }
    } else {
      triggerLabelEl.classList.toggle('prd-Form_VariantText-light', true)
      triggerLabelEl.textContent = triggerLabelEl.dataset.add

      triggerCostEl.textContent = ''
    }

    window.BAO.additionalCosts = personalisationCost

    if (this.required === false) {
      document.querySelector('site-drawers')?.closeDrawer()

      window.BAO.dispatchEvent(window.BAO.EVENTS.PDP.UPDATE_PRICE)

      this.handlePriceUpdate()
    }
  }

  requiredFields (element, required = true) {
    element.classList.toggle(
      'drw-Personalisation_FieldInput-required',
      required,
    )
  }

  handlePriceUpdate () {
    // If there are variants, this is already handled by the variants.js script
    const hasVariants =
      document.querySelector('variant-radios') ||
      document.querySelector('variant-selects')
    if (hasVariants) return

    // If there are no variants, we need to update the price
    const basePrice = Number.parseFloat(this.dataset.basePrice || 0)
    for (const priceEl of this.externalEls.price?.elements ?? []) {
      priceEl.innerHTML = formatMoney(basePrice + window.BAO.additionalCosts)
    }
  }

  get required () {
    return this._required
  }

  set required (val) {
    this._required = val
  }

  get addCharges () {
    return this.dataset.additionalCosts === 'true'
  }

  get formId () {
    return this.dataset.formId
  }
}

if (!customElements.get('product-engraving')) {
  customElements.define('product-engraving', ProductEngraving)
}
